import React, { FC, memo, useState, ChangeEventHandler } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import {
  InputField,
  InputFieldTextArea,
  CalendarField,
  SelectField,
} from '@studenten/ui-components';
import { UserProfile } from 'api_entities/user/types';
import { normalizePhoneValue } from '../../../../../lib/strings';
import {
  generalInfoValidationSchema,
  subjects,
  levels,
  visibility,
  ProfileVisibility,
} from './helpers';

import { Button, Form, InputWrapper } from './ProfileForm.styles';

export type GeneralInfoProps = {
  onSubmit: (values: Omit<GeneralInfoProps, 'onSubmit' | 'loading'>) => void;
  loading?: boolean;
} & Partial<
  Pick<
    UserProfile,
    | 'firstName'
    | 'lastName'
    | 'about'
    | 'phoneNumber'
    | 'city'
    | 'studySubject'
    | 'levelOfStudy'
    | 'startStudy'
    | 'endStudy'
    | 'consent'
  >
>;

type FormValues = Omit<GeneralInfoProps, 'onSubmit' | 'loading' | 'consent'> & {
  consent: ProfileVisibility;
};

const GeneralInfo: FC<GeneralInfoProps> = ({
  firstName,
  lastName,
  about,
  phoneNumber,
  levelOfStudy,
  studySubject,
  city,
  startStudy,
  endStudy,
  consent,
  onSubmit,
  loading,
}) => {
  const [studyPeriodDate, setStudyPeriodDate] = useState<Array<Date> | null>(() => {
    if (startStudy && endStudy) {
      return [new Date(startStudy), new Date(endStudy)];
    } else {
      return null;
    }
  });

  const { handleSubmit, formState, register, setValue, watch } = useForm<FormValues>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: {
      firstName: firstName || '',
      lastName: lastName || '',
      about: about || '',
      levelOfStudy: levelOfStudy || '',
      studySubject: studySubject || '',
      city: city || '',
      phoneNumber: phoneNumber || '',
      startStudy: startStudy ? new Date(startStudy).toISOString() : undefined,
      endStudy: endStudy ? new Date(endStudy).toISOString() : undefined,
      consent: consent ? ProfileVisibility.Visible : ProfileVisibility.Invisible,
    },
    resolver: yupResolver(generalInfoValidationSchema),
  });

  const { errors, isSubmitting } = formState;

  const handleChangeCalendar = (data: Array<Date>) => {
    setValue('startStudy', data[0].toISOString());
    setValue('endStudy', data[1].toISOString());
    setStudyPeriodDate(data);
  };

  const handlePhoneChange: ChangeEventHandler<HTMLInputElement> = ({ target }) => {
    setValue('phoneNumber', target.value);
  };

  const handleFormSubmit = ({
    levelOfStudy,
    studySubject,
    startStudy,
    endStudy,
    consent,
    ...values
  }: FormValues) => {
    const payload = {
      ...values,
      ...(levelOfStudy && { levelOfStudy }),
      ...(studySubject && { studySubject }),
      ...(startStudy && { startStudy }),
      ...(endStudy && { endStudy }),
      consent: consent === ProfileVisibility.Visible,
    };

    onSubmit(payload);
  };

  return (
    <Form onSubmit={handleSubmit(handleFormSubmit)}>
      <InputWrapper>
        <InputField
          label="Voornaam"
          placeholder="Vul je voornaam in"
          {...register('firstName')}
          error={errors.firstName?.message}
          withErrorMessage={!!errors.firstName}
          touched={!!errors.firstName}
        />
      </InputWrapper>

      <InputWrapper>
        <InputField
          label="Achternaam"
          placeholder="Vul je achternaam in"
          {...register('lastName')}
          error={errors.lastName?.message}
          withErrorMessage={!!errors.lastName}
          touched={!!errors.lastName}
        />
      </InputWrapper>
      <InputWrapper>
        <InputFieldTextArea
          label="About Me"
          {...register('about')}
          error={errors.about?.message}
          withErrorMessage={!!errors.about}
          touched={!!errors.about}
        />
      </InputWrapper>

      <InputWrapper>
        <InputField
          type="tel"
          label="Telefoonnummer"
          placeholder="Vul je telefoonnummer in"
          onChange={handlePhoneChange}
          value={normalizePhoneValue(watch('phoneNumber') || '')}
          error={errors.phoneNumber?.message}
          withErrorMessage={!!errors.phoneNumber}
          touched={!!errors.phoneNumber}
        />
      </InputWrapper>
      <InputWrapper>
        <InputField
          label="Stad"
          {...register('city')}
          error={errors.city?.message}
          withErrorMessage={!!errors.city}
          touched={!!errors.city}
        />
      </InputWrapper>
      <InputWrapper style={{ zIndex: 5 }}>
        <CalendarField
          label="Studeer periode"
          value={studyPeriodDate}
          onChange={handleChangeCalendar}
          format="dd.MM.yyyy"
        />
      </InputWrapper>
      <InputWrapper style={{ zIndex: 4 }}>
        <SelectField
          label="Studieniveau"
          options={levels}
          value={watch('levelOfStudy') || ''}
          onChange={(option) => setValue('levelOfStudy', option?.value || '')}
          error={errors.levelOfStudy?.message}
          withErrorMessage={!!errors.levelOfStudy}
          touched={!!errors.levelOfStudy}
          isSearchable={false}
        />
      </InputWrapper>
      <InputWrapper style={{ zIndex: 3 }}>
        <SelectField
          label="Studieonderwerp"
          options={subjects}
          value={watch('studySubject') || ''}
          onChange={(option) => setValue('studySubject', option?.value || '')}
          error={errors.studySubject?.message}
          withErrorMessage={!!errors.studySubject}
          touched={!!errors.studySubject}
          isSearchable={false}
        />
      </InputWrapper>
      <InputWrapper style={{ zIndex: 2 }}>
        <SelectField
          label="Zichtbaarheid"
          options={visibility}
          value={watch('consent')}
          onChange={(option) => {
            const value = option?.value || ProfileVisibility.Invisible;
            setValue('consent', value as ProfileVisibility);
          }}
          error={errors.consent?.message}
          withErrorMessage={!!errors.consent}
          touched={!!errors.consent}
          isSearchable={false}
        />
      </InputWrapper>
      <Button disabled={isSubmitting || loading}>Update Information</Button>
    </Form>
  );
};

export default memo(GeneralInfo);
